import type { ButtonHTMLAttributes, CSSProperties } from "react";
import { useIsMobile } from "../../lib/useMediaQuery";

type Variant = "primary" | "secondary" | "danger" | "ghost";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

const VARIANT_STYLE: Record<Variant, CSSProperties> = {
  primary: {
    background: "var(--accent)",
    border: "1px solid var(--accent)",
    color: "white",
  },
  secondary: {
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    color: "var(--text-secondary)",
  },
  danger: {
    background: "rgba(239,68,68,0.12)",
    border: "1px solid rgba(239,68,68,0.5)",
    color: "var(--error)",
  },
  ghost: {
    background: "transparent",
    border: "1px solid transparent",
    color: "var(--text-secondary)",
  },
};

/**
 * Touch-friendly button wrapping the app's existing secondaryBtn()/primary
 * recipes. On mobile it guarantees min-height: var(--tap-min) (44px). The
 * caller's own `style` prop wins last so consumers can still tweak per-use.
 */
export function Button({
  variant = "secondary",
  style,
  disabled,
  ...rest
}: ButtonProps) {
  const isMobile = useIsMobile();
  const base: CSSProperties = {
    padding: "7px 14px",
    borderRadius: 8,
    fontSize: 11.5,
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    whiteSpace: "nowrap",
    fontFamily: "var(--font-sans)",
    minHeight: isMobile ? "var(--tap-min)" : undefined,
    ...VARIANT_STYLE[variant],
  };
  return <button disabled={disabled} style={{ ...base, ...style }} {...rest} />;
}
